/*************************************************************************
 *
 * c20g CONFIDENTIAL
 * __________________
 *
 *  [2007] - [2021] Counterpoint Consulting, Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Counterpoint Consulting Incorporated.
 * The intellectual and technical concepts contained
 * herein are proprietary to Counterpoint Consulting Incorporated
 * and its suppliers and may be covered by U.S. and Foreign Patents,
 * patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Counterpoint Consulting Incorporated.
 */

import Backbone from 'backbone';
import { View } from 'backbone.marionette';

import modelGet from '../../util/modelGet';

import FileUploadTableGridView from './FileUploadTableGridView';
import './FileUploadTableView.css';
import tmplFileUploadTableView from './FileUploadTableView.hbs';

const FileUploadTableView = View.extend({
  template: tmplFileUploadTableView,

  className: 'file-upload-table-view',

  ui: {
    addFileRow: '.add-file-row',
    fileGridContainer: '.file-grid-container',
    validationAlert: '.validation-alert',
    validationAlertText: '.validation-alert-text',
  },

  regions: {
    fileGridContainer: '@ui.fileGridContainer',
  },

  events: {
    'click @ui.addFileRow': 'onClickAddFileRow',
  },

  options: {
    displayAddButton: true,
    addButtonClass: 'btn-primary',
  },

  templateContext() {
    return {
      addButtonClass: this.options.addButtonClass,
    };
  },

  onRender() {
    const { isReadOnly, documentTypes, emptyText, displayAddButton } = this.options;
    if (!this.collection) {
      this.collection = new Backbone.Collection();
    }
    this.fileUploadTableGridView = new FileUploadTableGridView({
      isReadOnly,
      documentTypes,
      emptyText,
      collection: this.collection,
    });
    if (isReadOnly || !displayAddButton) {
      this.ui.addFileRow.hide();
    }
    this.showChildView('fileGridContainer', this.fileUploadTableGridView);

    if (!isReadOnly) {
      this.$('.table').addClass('table-bordered');
    }
  },

  onClickAddFileRow(e) {
    e.preventDefault();
    this.collection.add({});
  },

  validate(validationConfig, minNumberOfFiles) {
    this.ui.validationAlertText.empty();
    this.ui.validationAlert.hide();
    let missingDocuments = false;
    let missingDocumentTypes = false;
    let missingRequiredNumberOfFiles = false;
    this.collection.each((model) => {
      const document = model.toJSON();
      if (!document.file) {
        missingDocuments = true;
      } else if (!document.file[0]) {
        missingDocuments = true;
      } else if (!document.file[0].fileId) {
        missingDocuments = true;
      }
      if (!document.documentType) {
        missingDocumentTypes = true;
      }
    });
    if (validationConfig && minNumberOfFiles) {
      if (validationConfig.type === 'submit') {
        const hasRequiredNumberOfFiles = this.hasNumberOfFiles(minNumberOfFiles);
        if (!hasRequiredNumberOfFiles) {
          missingRequiredNumberOfFiles = true;
        }
      }
    }
    if (missingDocuments) {
      this.ui.validationAlertText.append('Some rows are missing documents, each must have a document uploaded. ');
    }
    if (missingDocumentTypes) {
      this.ui.validationAlertText.append('Some rows are missing document types, each must have a document type set. ');
    }
    if (missingRequiredNumberOfFiles) {
      this.ui.validationAlertText.append('At least one document must be provided. ');
    }
    if (missingDocuments || missingDocumentTypes || missingRequiredNumberOfFiles) {
      this.ui.validationAlert.show();
      return Promise.reject();
    }
    return Promise.resolve();
  },

  getFormData() {
    return this.collection.reduce((memo, model) => {
      const fileId = modelGet(model, 'file[0].fileId');
      const fileName = modelGet(model, 'file[0].fileName');
      const documentTypeId = model.get('documentType');
      if (fileId && fileName) {
        memo.push({
          fileId,
          fileName,
          documentTypeId,
        });
      }
      return memo;
    }, []);
  },

  hasNumberOfFiles(numberOfFilesRequired) {
    const result = this.getFormData();
    return result.length >= numberOfFilesRequired;
  },
});

export default FileUploadTableView;
